import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { GoogleMap, InfoWindowF, MarkerF, useGoogleMap } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { ImageField, RichTextField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  isGoogleMapsMapReady,
  loadGoogleMapsMapLibrary,
} from 'lib/utils/googleMaps/loadGoogleMapsMapLibrary';
import { RootState } from 'src/store/store';
import { LocationSearchCoords } from '../LocationSearch.types';
import LocationSearchMapInfoWindow from './locationSearchMapInfoWindow';
import {
  applyLocationSearchMapViewport,
  resolveLocationSearchMapBounds,
} from './locationSearchMapBounds';
import { revealSelectedSiteOnMap } from './locationSearchMapSelection';
import { SiteCard } from './mapCoveoResultToCard.types';
import { StMapBlock, StMapStatusMessage, StMapPanel } from './mapCoveoResultToCard.styles';

/** Shift InfoWindow above the marker pin so the tooltip does not cover it. */
const MAP_INFO_WINDOW_PIXEL_OFFSET_Y = -56;

type MapLoadStatus = 'loading' | 'ready' | 'unavailable' | 'error';

/** Detect Google Maps modular loader failures (e.g. script blocked / double-loaded). */
export function isGoogleMapsModularLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.includes('Loader.provide not called');
}

function getMapInfoWindowOptions(): google.maps.InfoWindowOptions | undefined {
  if (!isGoogleMapsMapReady()) {
    return undefined;
  }

  return {
    pixelOffset: new google.maps.Size(0, MAP_INFO_WINDOW_PIXEL_OFFSET_Y),
  };
}

type LocationSearchResultsMapProps = {
  sites: SiteCard[];
  coords?: LocationSearchCoords | null;
  distanceMiles?: number;
  mapLocationIcon?: ImageField;
  markerTooltipText?: string;
  viewSiteLabel?: string;
  siteNameFormat?: RichTextField;
  hasLocationContext?: boolean;
  searchLocality?: string;
  selectedSiteId?: string | null;
  selectionTick?: number;
  onSiteSelect?: (siteId: string) => void;
};

type ClusteredMarkersProps = {
  sites: SiteCard[];
  markerIconUrl?: string;
  selectedSiteId?: string | null;
  selectionTick?: number;
  onMarkerHover: (siteId: string) => void;
  onMarkerClick: (siteId: string) => void;
  onMarkersError: () => void;
};

function filterSitesWithCoords(sites: SiteCard[]): SiteCard[] {
  return sites.filter(
    (site) =>
      typeof site.latitude === 'number' &&
      typeof site.longitude === 'number' &&
      !Number.isNaN(site.latitude) &&
      !Number.isNaN(site.longitude)
  );
}

function clearMarkerListeners(marker: google.maps.Marker): void {
  if (isGoogleMapsMapReady()) {
    google.maps.event.clearInstanceListeners(marker);
  }
}

function ClusteredMarkers({
  sites,
  markerIconUrl,
  selectedSiteId,
  selectionTick = 0,
  onMarkerHover,
  onMarkerClick,
  onMarkersError,
}: ClusteredMarkersProps): null {
  const map = useGoogleMap();
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersBySiteIdRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const onMarkerHoverRef = useRef(onMarkerHover);
  const onMarkerClickRef = useRef(onMarkerClick);
  const onMarkersErrorRef = useRef(onMarkersError);

  onMarkerHoverRef.current = onMarkerHover;
  onMarkerClickRef.current = onMarkerClick;
  onMarkersErrorRef.current = onMarkersError;

  const sitesWithCoords = useMemo(() => filterSitesWithCoords(sites), [sites]);

  useEffect(() => {
    if (!map || !isGoogleMapsMapReady()) {
      return;
    }

    markersBySiteIdRef.current.forEach((marker) => {
      clearMarkerListeners(marker);
      marker.setMap(null);
    });
    markersBySiteIdRef.current.clear();
    clustererRef.current?.clearMarkers();

    const icon = markerIconUrl ? { url: markerIconUrl } : undefined;
    const markers: google.maps.Marker[] = [];

    try {
      sitesWithCoords.forEach((site) => {
        const marker = new google.maps.Marker({
          position: { lat: site.latitude!, lng: site.longitude! },
          icon,
          zIndex: 1,
        });

        marker.addListener('click', () => {
          onMarkerClickRef.current(site.id);
        });

        marker.addListener('mouseover', () => {
          onMarkerHoverRef.current(site.id);
        });

        markersBySiteIdRef.current.set(site.id, marker);
        markers.push(marker);
      });

      clustererRef.current = new MarkerClusterer({ map, markers });
    } catch {
      markers.forEach((marker) => {
        clearMarkerListeners(marker);
        marker.setMap(null);
      });
      markersBySiteIdRef.current.clear();
      clustererRef.current = null;
      onMarkersErrorRef.current();
      return;
    }

    return () => {
      clustererRef.current?.clearMarkers();
      markersBySiteIdRef.current.forEach((marker) => {
        clearMarkerListeners(marker);
        marker.setMap(null);
      });
      markersBySiteIdRef.current.clear();
      clustererRef.current = null;
    };
  }, [map, markerIconUrl, sitesWithCoords]);

  useEffect(() => {
    markersBySiteIdRef.current.forEach((marker, siteId) => {
      marker.setZIndex(selectedSiteId === siteId ? 1000 : 1);
    });
  }, [selectedSiteId]);

  useEffect(() => {
    if (!map || !selectedSiteId || !isGoogleMapsMapReady()) {
      return;
    }

    const marker = markersBySiteIdRef.current.get(selectedSiteId);
    if (!marker) {
      return;
    }

    const controller = new AbortController();
    void revealSelectedSiteOnMap({ map, marker, signal: controller.signal });

    return () => {
      controller.abort();
    };
  }, [map, selectedSiteId, selectionTick]);

  return null;
}

export const resolveMapInfoSiteId = (
  isInfoWindowDismissed: boolean,
  activeSiteId: string | null,
  selectedSiteId?: string | null
): string | null => (isInfoWindowDismissed ? null : (activeSiteId ?? selectedSiteId ?? null));

function LocationSearchResultsMap({
  sites,
  coords,
  distanceMiles,
  mapLocationIcon,
  markerTooltipText,
  viewSiteLabel = 'View site',
  siteNameFormat,
  hasLocationContext = false,
  searchLocality,
  selectedSiteId,
  selectionTick = 0,
  onSiteSelect,
}: LocationSearchResultsMapProps): JSX.Element {
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings = sitecoreContext?.siteSettings as {
    countryLatitude?: { value?: string };
    countryLongitude?: { value?: string };
    googleMapsAPIKey?: { value?: string };
  };
  const googleScriptLoaded = useSelector((state: RootState) =>
    Boolean(state.geolocation?.googleScriptLoaded)
  );

  const [mapLoadStatus, setMapLoadStatus] = useState<MapLoadStatus>(() =>
    isGoogleMapsMapReady() ? 'ready' : 'loading'
  );
  const [isMapInstanceReady, setIsMapInstanceReady] = useState(false);
  const [activeSiteId, setActiveSiteId] = useState<string | null>(null);
  const [isInfoWindowDismissed, setIsInfoWindowDismissed] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerIconUrl = mapLocationIcon?.value?.src;

  const handleInfoWindowClose = useCallback(() => {
    setActiveSiteId(null);
    setIsInfoWindowDismissed(true);
  }, []);

  const handleMarkerHover = useCallback((siteId: string) => {
    setIsInfoWindowDismissed(false);
    setActiveSiteId(siteId);
  }, []);

  const handleMarkerClick = useCallback(
    (siteId: string) => {
      setIsInfoWindowDismissed(false);
      onSiteSelect?.(siteId);
      setActiveSiteId(siteId);
    },
    [onSiteSelect]
  );

  const handleMarkersError = useCallback(() => {
    setMapLoadStatus('error');
  }, []);

  const mapApiKey = useMemo(() => {
    try {
      return siteSettings?.googleMapsAPIKey?.value
        ? atob(siteSettings.googleMapsAPIKey.value)
        : undefined;
    } catch {
      return undefined;
    }
  }, [siteSettings?.googleMapsAPIKey?.value]);

  useEffect(() => {
    let cancelled = false;

    const ensureMapLibrary = async () => {
      if (isGoogleMapsMapReady()) {
        setMapLoadStatus('ready');
        return;
      }

      // Layout already finished Maps bootstrap via useGeoLocation — reuse, do not cold-inject.
      if (googleScriptLoaded) {
        setMapLoadStatus('loading');
        try {
          await loadGoogleMapsMapLibrary(mapApiKey);
          if (!cancelled) {
            setMapLoadStatus(isGoogleMapsMapReady() ? 'ready' : 'error');
          }
        } catch {
          if (!cancelled) {
            setMapLoadStatus('error');
          }
        }
        return;
      }

      if (!mapApiKey) {
        setMapLoadStatus('unavailable');
        return;
      }

      setMapLoadStatus('loading');

      try {
        await loadGoogleMapsMapLibrary(mapApiKey);
        if (cancelled) {
          return;
        }
        setMapLoadStatus(isGoogleMapsMapReady() ? 'ready' : 'error');
      } catch {
        if (!cancelled) {
          setMapLoadStatus('error');
        }
      }
    };

    void ensureMapLibrary();

    return () => {
      cancelled = true;
    };
  }, [mapApiKey, googleScriptLoaded]);

  // Modular Maps chunks can fail after Map constructor is present (e.g. Loader.provide).
  useEffect(() => {
    if (mapLoadStatus !== 'ready') {
      return;
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isGoogleMapsModularLoadError(event.reason)) {
        setMapLoadStatus('error');
      }
    };

    const handleWindowError = (event: ErrorEvent) => {
      if (isGoogleMapsModularLoadError(event.error ?? event.message)) {
        setMapLoadStatus('error');
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleWindowError);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleWindowError);
    };
  }, [mapLoadStatus]);

  useEffect(() => {
    if (selectedSiteId) {
      setIsInfoWindowDismissed(false);
      setActiveSiteId(selectedSiteId);
    }
  }, [selectedSiteId, selectionTick]);

  const sitesWithCoords = useMemo(() => filterSitesWithCoords(sites), [sites]);

  const infoSiteId = resolveMapInfoSiteId(isInfoWindowDismissed, activeSiteId, selectedSiteId);
  const infoSite = sitesWithCoords.find((site) => site.id === infoSiteId) ?? null;
  const infoWindowOptions = useMemo(() => {
    if (mapLoadStatus !== 'ready' || !infoSite) {
      return undefined;
    }
    return getMapInfoWindowOptions();
  }, [mapLoadStatus, infoSite]);

  useEffect(() => {
    if (!infoSite) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleInfoWindowClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleInfoWindowClose, infoSite]);

  const center = useMemo(() => {
    if (coords) {
      return { lat: coords.lat, lng: coords.lng };
    }
    const first = sitesWithCoords[0];
    if (first) {
      return { lat: first.latitude!, lng: first.longitude! };
    }
    return {
      lat: Number(siteSettings?.countryLatitude?.value) || 58.3,
      lng: Number(siteSettings?.countryLongitude?.value) || -134.43,
    };
  }, [
    coords,
    sitesWithCoords,
    siteSettings?.countryLatitude?.value,
    siteSettings?.countryLongitude?.value,
  ]);

  const initialCameraRef = useRef({ center, zoom: 10 });

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setIsMapInstanceReady(true);
  }, []);

  useEffect(() => {
    if (mapLoadStatus === 'ready') {
      return;
    }

    mapRef.current = null;
    setIsMapInstanceReady(false);
  }, [mapLoadStatus]);

  useEffect(() => {
    if (!isMapInstanceReady) {
      return;
    }

    applyLocationSearchMapViewport(
      mapRef.current,
      resolveLocationSearchMapBounds({
        coords,
        distanceMiles,
        hasLocationContext,
        sites: sitesWithCoords,
      })
    );
  }, [coords, distanceMiles, hasLocationContext, isMapInstanceReady, sitesWithCoords]);

  const mapContainerStyle = {
    width: '100%',
    height: '100%',
  };

  const handleMapClick = () => {
    handleInfoWindowClose();
  };

  const mapStatusMessage =
    mapLoadStatus === 'loading'
      ? 'Loading map…'
      : mapLoadStatus === 'unavailable'
        ? 'Map is unavailable.'
        : mapLoadStatus === 'error'
          ? 'Unable to load map.'
          : null;

  return (
    <StMapPanel>
      <StMapBlock>
        {mapLoadStatus === 'ready' ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={initialCameraRef.current.center}
            zoom={initialCameraRef.current.zoom}
            options={{ scrollwheel: false, mapTypeControl: false }}
            onLoad={handleMapLoad}
            onClick={handleMapClick}
          >
            {coords && <MarkerF position={{ lat: coords.lat, lng: coords.lng }} />}
            <ClusteredMarkers
              sites={sites}
              markerIconUrl={markerIconUrl}
              selectedSiteId={selectedSiteId}
              selectionTick={selectionTick}
              onMarkerHover={handleMarkerHover}
              onMarkerClick={handleMarkerClick}
              onMarkersError={handleMarkersError}
            />
            {infoSite && (
              <InfoWindowF
                key={infoSite.id}
                position={{ lat: infoSite.latitude!, lng: infoSite.longitude! }}
                options={infoWindowOptions}
                onCloseClick={handleInfoWindowClose}
              >
                <LocationSearchMapInfoWindow
                  site={infoSite}
                  viewSiteLabel={viewSiteLabel}
                  markerTooltipText={markerTooltipText}
                  hasLocationContext={hasLocationContext}
                  searchLocality={searchLocality}
                  siteNameFormat={siteNameFormat}
                  onClose={handleInfoWindowClose}
                />
              </InfoWindowF>
            )}
          </GoogleMap>
        ) : (
          mapStatusMessage && (
            <StMapStatusMessage role="status">{mapStatusMessage}</StMapStatusMessage>
          )
        )}
      </StMapBlock>
    </StMapPanel>
  );
}

export default LocationSearchResultsMap;
